<template>
  <div
    class="drawer-backdrop"
    @click.self="close"
  ></div>
  <aside
    class="world-side-menu"
    :class="{ 'materialize-anim': shouldAnimate }"
    @pointerdown.stop
    @animationend="shouldAnimate = false"
  >
    <div class="panel-content glass-panel">
      <header class="drawer-header">
        <h2>{{ headerTitle }}</h2>
        <button type="button" class="drawer-close" title="Close" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="tab-content">
        <template v-if="empire?.isSelf">
          <p class="city-name">{{ empire.cityName || "Your city" }}</p>
          <p class="hint">This is your empire. Select an enemy hex to send scouts.</p>
        </template>

        <template v-else>
          <div class="stat-row">
            <span class="stat-label">Empire</span>
            <span class="stat-value">{{ empire.nickname || "Unknown" }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">City</span>
            <span class="stat-value">{{ empire.cityName || "—" }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Race</span>
            <span class="stat-value race">{{ empire.race || "—" }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Distance</span>
            <span class="stat-value">{{ distance }} tiles</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Travel</span>
            <span class="stat-value">{{ travelLabel }}</span>
          </div>

          <div v-if="targetShielded" class="truce-banner" role="status">
            <span class="truce-label">
              <i class="fas fa-shield-halved" aria-hidden="true"></i>
              Protected under Truce
            </span>
            <span class="truce-timer">{{ targetShieldCountdown }}</span>
          </div>

          <button
            type="button"
            class="send-btn march-btn"
            :disabled="targetShielded"
            @click="marchOnEmpire"
          >
            {{ targetShielded ? "March Locked" : "March" }}
          </button>

          <section class="scout-block">
            <label class="place-label" for="scout-qty">Send Scouts</label>
            <p class="stock-line">
              Available:
              <strong>{{ scoutStock }}</strong>
            </p>
            <div class="slider-row">
              <input
                id="scout-qty-range"
                type="range"
                min="1"
                :max="sliderMax"
                :value="scoutQty"
                :disabled="scoutStock < 1 || gameStore.isScouting"
                @input="setQty($event.target.value)"
              />
              <input
                id="scout-qty"
                class="qty-input"
                type="number"
                min="1"
                :max="sliderMax"
                :value="scoutQty"
                :disabled="scoutStock < 1 || gameStore.isScouting"
                @input="setQty($event.target.value)"
              />
            </div>
            <button
              type="button"
              class="send-btn"
              :disabled="!canSend"
              @click="sendScouts"
            >
              {{ gameStore.isScouting ? "Sending…" : `Send ${scoutQty}` }}
            </button>
            <p v-if="gameStore.scoutError" class="save-error">
              {{ gameStore.scoutError }}
            </p>
          </section>

          <section v-if="activeMissions.length" class="mission-block">
            <h3>In Flight</h3>
            <ul>
              <li v-for="mission in activeMissions" :key="mission.id">
                <span>{{ mission.scout_count }} scouts</span>
                <span class="eta">{{ formatEta(mission.arrive_at) }}</span>
              </li>
            </ul>
          </section>

          <section class="stack-block">
            <h3>Intel Reports</h3>
            <IntelReportStack
              :reports="empireReports"
              :selected-id="openReport?.id"
              empty-text="No intel reports for this empire yet."
              @select="openIntel"
            />
          </section>
        </template>
      </div>
    </div>

    <IntelReportModal :report="openReport" @close="openReport = null" />
    <BattleDetailedReportModal
      :is-visible="!!openBattle"
      :battle-result="openBattle?.result || null"
      :attacker-race="openBattle?.result?.attackerRace || ''"
      :defender-race="openBattle?.result?.defenderRace || ''"
      :perspective="openBattle?.perspective || 'defender'"
      @close="openBattle = null"
    />
  </aside>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useGameStore } from "../store/game";
import { useUiStore } from "../store/ui";
import IntelReportStack from "./IntelReportStack.vue";
import IntelReportModal from "./IntelReportModal.vue";
import BattleDetailedReportModal from "./BattleDetailedReportModal.vue";
import {
  formatShieldCountdown,
  isCityProtected,
} from "../utils/cityShield";
import {
  battleReportIdFromIntel,
  isDefenseReport,
} from "../utils/intelReports";

const router = useRouter();
const gameStore = useGameStore();
const uiStore = useUiStore();

const shouldAnimate = ref(true);
const scoutQty = ref(1);
const openReport = ref(null);
const openBattle = ref(null);
const nowMs = ref(Date.now());
let timerId = null;

const empire = computed(() => gameStore.selectedWorldEmpire);
const headerTitle = computed(() =>
  empire.value?.isSelf ? "Your Empire" : "World Intel"
);

const selfEmpire = computed(
  () => (gameStore.worldMap || []).find((entry) => entry.isSelf) || null
);

const distance = computed(() => {
  const from = selfEmpire.value;
  const to = empire.value;
  if (!from || !to) return 1;
  const dq = Number(to.pos_x) - Number(from.pos_x);
  const dr = Number(to.pos_y) - Number(from.pos_y);
  return Math.max(1, (Math.abs(dq) + Math.abs(dr) + Math.abs(-dq - dr)) / 2);
});

const travelSecondsPerHex = computed(() =>
  Math.max(1, Math.floor(Number(gameStore.travelSecondsPerHex) || 60))
);

const travelLabel = computed(() =>
  formatDuration(distance.value * travelSecondsPerHex.value)
);

const scoutStock = computed(() => gameStore.scoutReserve);
const sliderMax = computed(() => Math.max(1, scoutStock.value));

const canSend = computed(
  () =>
    !empire.value?.isSelf &&
    scoutStock.value >= 1 &&
    scoutQty.value >= 1 &&
    scoutQty.value <= scoutStock.value &&
    !gameStore.isScouting
);

const activeMissions = computed(() => {
  const id = Number(empire.value?.id);
  if (!Number.isFinite(id)) return [];
  return (gameStore.scoutingMissions || []).filter(
    (mission) => Number(mission.defender_id) === id
  );
});

const empireReports = computed(() => {
  const id = Number(empire.value?.id);
  if (!Number.isFinite(id)) return [];
  return (gameStore.intelReports || []).filter(
    (report) =>
      (report.kind === "scout_intel" ||
        report.kind === "counter_intel" ||
        report.kind === "defense_report") &&
      Number(report.other_user_id) === id
  );
});

const targetShielded = computed(() =>
  isCityProtected(
    empire.value?.protected_until,
    gameStore.clockOffsetMs,
    nowMs.value
  )
);

const targetShieldCountdown = computed(() =>
  formatShieldCountdown(
    empire.value?.protected_until,
    gameStore.clockOffsetMs,
    nowMs.value
  )
);

function setQty(value) {
  const n = Math.floor(Number(value) || 0);
  const max = scoutStock.value;
  if (max < 1) {
    scoutQty.value = 1;
    return;
  }
  scoutQty.value = Math.min(max, Math.max(1, n));
}

function close() {
  gameStore.selectWorldEmpire(null);
  uiStore.setSidePanelOpen(false);
}

async function sendScouts() {
  if (!canSend.value || !empire.value) return;
  try {
    await gameStore.startScout(empire.value.id, scoutQty.value);
    setQty(1);
  } catch {
    /* scoutError is on the store */
  }
}

function marchOnEmpire() {
  if (!empire.value || targetShielded.value) return;
  router.push({
    path: "/game/battle",
    query: { empire: String(empire.value.id) },
  });
}

async function openIntel(report) {
  if (report?.id && !report.read_at) {
    try {
      const updated = await gameStore.markIntelRead(report.id);
      if (updated) report = updated;
    } catch {
      /* keep local report */
    }
  }
  if (isDefenseReport(report)) {
    openReport.value = null;
    const battleId = battleReportIdFromIntel(report);
    if (!battleId) return;
    try {
      openBattle.value = await gameStore.fetchBattleReport(battleId);
    } catch {
      openBattle.value = null;
    }
    return;
  }
  openBattle.value = null;
  openReport.value = report;
}

function formatDuration(totalSeconds) {
  const n = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const minutes = Math.floor(n / 60);
  const seconds = n % 60;
  if (minutes < 1) return `${seconds}s`;
  if (seconds === 0) return minutes === 1 ? "1 min" : `${minutes} min`;
  return `${minutes}m ${seconds}s`;
}

function formatEta(arriveAt) {
  const end = new Date(arriveAt).getTime();
  if (!Number.isFinite(end)) return "";
  const diff = end - (nowMs.value + (gameStore.clockOffsetMs || 0));
  if (diff <= 0) return "Arriving…";
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

watch(scoutStock, (stock) => {
  if (scoutQty.value > stock) setQty(stock);
});

watch(
  () => empire.value?.id,
  () => {
    shouldAnimate.value = true;
    openReport.value = null;
    setQty(Math.min(scoutQty.value, Math.max(1, scoutStock.value)));
  }
);

onMounted(() => {
  uiStore.setSidePanelOpen(true);
  timerId = setInterval(async () => {
    nowMs.value = Date.now();
    const due = activeMissions.value.some((mission) => {
      const end = new Date(mission.arrive_at).getTime();
      return Number.isFinite(end) && end <= nowMs.value + (gameStore.clockOffsetMs || 0);
    });
    if (!due) return;
    try {
      await Promise.all([
        gameStore.fetchScoutingMissions(),
        gameStore.fetchWorldIntel(),
        gameStore.fetchPlayerResources(),
      ]);
    } catch {
      /* overlay / stock refresh is best-effort */
    }
  }, 1000);
});

onUnmounted(() => {
  if (timerId) clearInterval(timerId);
});
</script>

<style scoped>
.world-side-menu {
  position: fixed;
  right: 0;
  top: 70px;
  bottom: 80px;
  width: 350px;
  z-index: 800;
  display: flex;
  flex-direction: column;
}

.materialize-anim {
  animation: panel-materialize 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  opacity: 0;
}

@keyframes panel-materialize {
  0% {
    opacity: 0;
    transform: translateX(50px) scale(0.95);
    filter: blur(10px) brightness(2);
  }
  50% {
    opacity: 0.8;
    filter: blur(2px) brightness(1.5);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
    filter: blur(0) brightness(1);
  }
}

.glass-panel {
  flex-grow: 1;
  background-color: rgba(20, 20, 30, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-left: 1px solid rgba(0, 246, 255, 0.3);
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(0, 246, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid rgba(0, 246, 255, 0.2);
  background: rgba(36, 40, 59, 0.6);
}

.drawer-header h2 {
  margin: 0;
  font-size: 1rem;
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
}

.drawer-close {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  line-height: 1;
  background: none;
  border: 1px solid rgba(0, 246, 255, 0.3);
  color: #a0a0b0;
  border-radius: 6px;
  cursor: pointer;
}

.drawer-close:hover {
  color: #00f6ff;
  border-color: #00f6ff;
}

.tab-content {
  flex-grow: 1;
  padding: 16px 18px 20px;
  overflow-y: auto;
  color: #e0e0e0;
}

.city-name {
  margin: 0 0 0.5rem;
  font-family: "Cinzel", serif;
  font-size: 1.15rem;
  color: #c9b48a;
  text-align: center;
}

.hint {
  margin: 0;
  color: #8892b0;
  font-size: 0.85rem;
  text-align: center;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.9rem;
}

.stat-label {
  color: #a0a0b0;
}

.stat-value {
  font-weight: 600;
  color: #fff;
}

.stat-value.race {
  text-transform: capitalize;
}

.truce-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.85rem;
  padding: 0.55rem 0.65rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 240, 255, 0.4);
  background: rgba(0, 32, 48, 0.75);
  box-shadow: 0 0 12px rgba(0, 240, 255, 0.2);
  color: #9eefff;
}

.truce-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.truce-timer {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: #e7ffff;
}

.march-btn {
  background: rgba(0, 240, 255, 0.12);
  border: 1px solid #00f0ff;
  color: #00f0ff;
}

.scout-block,
.mission-block,
.stack-block {
  margin-top: 1.15rem;
}

.place-label,
.mission-block h3,
.stack-block h3 {
  margin: 0 0 0.45rem;
  font-family: "Cinzel", serif;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9b48a;
}

.stock-line {
  margin: 0 0 0.45rem;
  font-size: 0.8rem;
  color: #8892b0;
}

.stock-line strong {
  color: #87ceeb;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

input[type="range"] {
  flex: 1;
  cursor: pointer;
  accent-color: #00f0ff;
}

.qty-input {
  width: 4.25rem;
  padding: 0.3rem 0.4rem;
  border-radius: 4px;
  border: 1px solid rgba(0, 246, 255, 0.35);
  background: rgba(8, 12, 24, 0.85);
  color: #00f0ff;
  font-family: monospace;
  font-size: 0.95rem;
  text-align: right;
}

.send-btn {
  width: 100%;
  margin-top: 0.7rem;
}

.send-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.save-error {
  margin: 0.5rem 0 0;
  color: #bf616a;
  font-size: 0.8rem;
}

.mission-block ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.mission-block li {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem;
  background: rgba(0, 240, 255, 0.08);
  border: 1px solid rgba(0, 240, 255, 0.22);
  border-radius: 4px;
  font-size: 0.8rem;
}

.eta {
  color: #88c0d0;
  font-variant-numeric: tabular-nums;
}

.drawer-backdrop {
  display: none;
}

@media (max-width: 768px) {
  .drawer-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 1190;
  }

  .world-side-menu {
    top: 0;
    right: 0;
    bottom: 0;
    width: 86vw;
    max-width: 380px;
    height: 100dvh;
    z-index: 1200;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.7);
    transform: translateX(0);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .materialize-anim {
    animation: none;
    opacity: 1;
  }

  .glass-panel {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
}
</style>
